import { ChangeDetectionStrategy, Component, effect, Injector, inject, input, output, untracked } from '@angular/core';
import { CtbRadioButtonComponent } from '@lepoleimmo/cost-to-build-client-ui-radio-button';
import { areRadioGroupsStatesEqual, RADIO_GROUPS_INITIAL_STATE } from './cost-to-build-client-ui-radio-groups.state';
import { RadioGroupsStore } from './cost-to-build-client-ui-radio-groups.store';
import type { RadioGroupOption, RadioGroupsState } from './cost-to-build-client-ui-radio-groups.types';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CtbRadioButtonComponent],
    providers: [RadioGroupsStore],
    selector: 'ctb-radio-groups',
    styleUrl: './cost-to-build-client-ui-radio-groups.scss',
    templateUrl: './cost-to-build-client-ui-radio-groups.html',
})
export class CtbRadioGroupsComponent {
    readonly #injector = inject(Injector);
    readonly #store = inject(RadioGroupsStore);

    readonly name = input<string>(RADIO_GROUPS_INITIAL_STATE.name);
    readonly options = input<readonly RadioGroupOption[]>(RADIO_GROUPS_INITIAL_STATE.options);

    readonly valueChange = output<string>();
    readonly stateChange = output<RadioGroupsState>();

    readonly state = this.#store.state;

    #lastEmittedState: RadioGroupsState | null = null;

    readonly syncStoreFromInputs = effect(() => this.#syncStoreFromInputs(), { injector: this.#injector });

    readonly emitStateChanges = effect(
        () => {
            const nextState = this.state();
            const previousState = this.#lastEmittedState;

            if (previousState !== null && areRadioGroupsStatesEqual(previousState, nextState)) {
                return;
            }

            this.#lastEmittedState = nextState;
            this.stateChange.emit(nextState);
        },
        { injector: this.#injector }
    );

    onOptionSelect(id: string) {
        const value = this.state().options.find((option) => option.id === id)?.value ?? '';

        this.#store.selectValue(value);
        this.valueChange.emit(value);
    }

    #syncStoreFromInputs() {
        const options = this.options();
        const name = this.name();

        untracked(() => {
            const resolvedValue = options.find((option) => option.checked)?.value ?? RADIO_GROUPS_INITIAL_STATE.value;

            this.#store.setState({ name, options, value: resolvedValue });
        });
    }
}
